import { trackAccountPage } from '@/api/accountLogs'
import { getRestAccess } from '@/api/restDay'
import { isBusinessRole } from '@/utils/roleAccess'
import { createRouter, createWebHashHistory, createWebHistory, RouteRecordRaw } from 'vue-router'
import LoginView from '../views/LoginView.vue'
import { useUserStore } from '@/store/user';
import { ElMessage } from 'element-plus'
import { isMobileDevice } from '@/utils/index';
import { showFailToast } from 'vant'
import pcRouter from '@/router/pcRouter'
import mbRouter from '@/router/mbRouter'
import lottRouter from '@/router/lottRouter'
import merchantRouter from '@/router/merchantRouter'
import { checkMerchantSession } from '@/api/merchant'
import { isMerchantHost, isMerchantPath, isMerchantRole, MERCHANT_LOGIN } from '@/utils/merchantAccess'


const routes: Array<RouteRecordRaw> = [
  { path: '/draw-control/:id', component: () => import('../views/public/DrawControl.vue'), meta: { title: '幸运一刻 · 模拟摇奖' } },
  ...pcRouter,
  ...mbRouter,
  ...lottRouter,
  ...merchantRouter,
  {
    path: '/mlogin',
    name: 'mLogin',
    meta: {
      title: '移动端登录'
    },
    component: ()=> import(/* webpackChunkName: "about" */ '../views/mobileView/mobileLogin.vue')
  },
  {
    path: '/home/login',
    name: 'Login',
    meta: {
      title: '登录'
    },
    component: LoginView
  },
  {
    path: '/',
    name: 'protal',
    meta: { title: '首页' },
    redirect: () => (isMerchantHost() ? '/merchant' : isMobileDevice() ? '/mlogin' : '/home/login')
  }
]

// 将路由模式修改为 非hash 模式
const router = createRouter({
  history: createWebHistory(),
  routes
})

function notifyRouteWarning(message: string) {
  if (isMobileDevice()) {
    showFailToast({ message, duration: 2500, forbidClick: true })
  } else {
    ElMessage.warning(message)
  }
}

// 设置一个白名单，不需要登录就能访问的页面
/** 这里豁免的是账号登录守卫。走势图页面仍由电视入口校验设备凭证，加入白名单不代表公开门店配置。 */
const whiteList = ['/normal-tv-menu', '/normal-football-board', '/home/login', '/mlogin', '/register', '/forgetPassword', '/mhome/plan-selector', '/football-match-board', '/menu-trend-chart', '/trend-chart-container', '/trend-chart', '/seven-start-game-trend-chart', '/ranking-game-trend-chart', '/mhome/trend-authorize']

// 全局前置守卫
/** 前端守卫负责入口与页面跳转体验；接口权限仍须由服务端按当前账号及有效门店关联重新校验。 */
router.beforeEach(async (to, from, next) => {
  if (to.path.startsWith('/draw-control/')) { next(); return }
  const userStore = useUserStore();
  if (to.path === MERCHANT_LOGIN) { next(); return }
  if (to.path === '/mhome/trend-authorize' && (!userStore.token || isMerchantRole(userStore.userInfo.roleInfo?.roleIdentifier))) { next({ path: '/trend-authorize', query: to.query, replace: true }); return }
  const merchantArea = isMerchantPath(to.path), authorizationBridge = to.path === '/trend-authorize'
  if (userStore.token && (merchantArea || authorizationBridge || isMerchantRole(userStore.userInfo.roleInfo?.roleIdentifier))) {
    try {
      const checked = await checkMerchantSession(userStore.token)
      userStore.setUserInfo({ ...checked.data, id: checked.data.userId })
    } catch (error: any) {
      if ([401, 403].includes(error.response?.status)) userStore.resetSession()
      else notifyRouteWarning('暂时无法验证登录，请检查网络后重试')
      next({ path: MERCHANT_LOGIN, query: { redirect: merchantArea || authorizationBridge ? to.fullPath : '/merchant' } }); return
    }
  }
  if (isMerchantRole(userStore.userInfo.roleInfo?.roleIdentifier) && !merchantArea && !authorizationBridge) { next({ path: '/merchant', replace: true }); return }
  if (merchantArea || authorizationBridge) {
    if (!userStore.token) { next({ path: MERCHANT_LOGIN, query: { redirect: to.fullPath } }); return }
    if (merchantArea && !isMerchantRole(userStore.userInfo.roleInfo?.roleIdentifier)) { next({ path: MERCHANT_LOGIN }); return }
    next(); return
  }
  // 在这里添加你的路由守卫逻辑
  // 例如检查用户是否登录
  const isLoggedIn = userStore.token !== '';

  // 判断是否为直接访问（非应用内跳转）
  const isDirectAccess = !from.name || from.path === '/';

  // 将直接访问信息传递给目标路由的 meta
  if (isDirectAccess) {
    to.meta.isDirectAccess = true;
  }

  // 1. 如果访问的是登录页面，直接放行
  if (whiteList.includes(to.path)) {
    next();
    return;
  }

  // 2. 检查用户是否登录，如果未登录，根据设备类型重定向到对应登录页
  if (!isLoggedIn) {
    notifyRouteWarning('您还未登录，请先登录后再访问');
    const targetLoginRoute = isMobileDevice() ? 'mLogin' : 'Login';
    next({ name: targetLoginRoute, query: { redirect: to.fullPath } });
    return;
  }

  // 3. 已登录用户，检查路由权限（仅 PC 后台路由需要；移动端登录后直接放行）
  if (to.meta.requiresBusiness && !isBusinessRole(userStore.userInfo.roleInfo?.roleIdentifier)) {
    notifyRouteWarning('仅管理员或销售员可访问此工具'); next({ path: '/mhome/self-page' }); return
  }
  if (to.meta.requiresAdmin) {
    if (userStore.userInfo.roleInfo?.roleIdentifier === 'admin') next()
    else { notifyRouteWarning(`仅管理员可访问${String(to.meta.title || '此页面')}`); next({ path: isMobileDevice() ? '/mhome/self-page' : '/home' }) }
    return
  }
  if (to.meta.requiresRestEmployee) {
    try {
      const access = await getRestAccess()
      if (access.allowed) next()
      else { notifyRouteWarning('仅管理员、销售员及在职员工可访问'); next({ path: '/mhome/self-page' }) }
    } catch { next({ path: '/mhome/self-page' }) }
    return
  }
  const routePermissions = userStore.userInfo?.routePermissions?.split(',') || [];
  // console.log('路由权限:', routePermissions);

  const isMobileRoute = to.path.startsWith('/mhome');

  // 移动端路由：登录即可访问；PC 路由：需要权限列表为空或在权限列表中
  const allowedAdmin = to.meta.allowAdmin === true && userStore.userInfo.roleInfo?.roleIdentifier === 'admin';
  const allowedBusiness = to.meta.allowBusiness === true && isBusinessRole(userStore.userInfo.roleInfo?.roleIdentifier);
  if (allowedAdmin || allowedBusiness || isMobileRoute || routePermissions.length === 0 || routePermissions.includes(to.path)) {
    next();
  } else {
    // 没有权限，重定向到登录页或其他无权限提示页
    notifyRouteWarning('没有权限访问该页面');
    next({ name: isMobileDevice() ? 'mLogin' : 'Login' });
  }
})

// 全局后置守卫
router.afterEach((to, from, failure) => {
  if (!failure && !to.path.startsWith('/draw-control/') && !['/home/login', '/mlogin'].includes(to.path)) trackAccountPage(to.path);
  // 在这里可以添加页面加载后的逻辑，如设置页面标题
  document.title = `${String(to.meta.title) || '首页'}`;
})

export default router
